import * as React from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../config/firebase";
import { AppError, mapFirebaseError } from "../utils/appError";
import { reportError } from "../utils/errorReporting";

/**
 * Failures that mean the flow worked as designed rather than broke, so they
 * are not worth a Sentry event: a state that expired or was already spent (a
 * user who left the consent screen open too long, or reloaded this route),
 * and a ShopVibes account without admin permissions being turned away by the
 * connect gate — that refusal is the gate doing its job.
 */
const EXPECTED_FAILURE_CODES = new Set([
  "shopvibes/state_invalid",
  "shopvibes/not_admin",
]);

interface ShopVibesCallbackSearch {
  code?: string;
  state?: string;
  error?: string;
}

/**
 * Where FusionAuth sends the browser back to after the ShopVibes consent
 * screen. Completing the link needs an authenticated request (the backend
 * checks the caller's Firebase uid against the uid sealed into the state),
 * which a third-party redirect straight into a Cloud Function can never
 * carry — so the redirect lands here, inside the signed-in SPA, and this
 * route finalizes the flow through the `finishShopVibesConnect` callable and
 * hands the outcome to Settings → Integrations → Shopvibes, which owns the
 * connection UI.
 */
const ShopVibesCallbackPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  // The OAuth `code` is single-use: a second exchange fails at FusionAuth
  // and would surface a spurious error. This latch is set synchronously
  // before the call, so a re-render or React StrictMode's double-invoked
  // effect cannot produce a second attempt.
  const hasStarted = React.useRef(false);

  const { code, state, error } = search;

  React.useEffect(() => {
    if (hasStarted.current) return;

    const goToError = (errorCode: string) => {
      // `replace` so the callback URL — which carries a now-spent code —
      // does not stay in history for a back-navigation to replay.
      navigate({
        to: "/settings/shopvibes",
        search: { shopvibes: "error", code: errorCode },
        replace: true,
      });
    };

    if (error) {
      hasStarted.current = true;
      goToError("shopvibes/oauth_denied");
      return;
    }

    if (!code || !state) {
      hasStarted.current = true;
      goToError("shopvibes/state_invalid");
      return;
    }

    // Arriving from an external redirect boots the SPA cold, so Firebase has
    // not finished rehydrating the session from IndexedDB yet and
    // `auth.currentUser` is still null. onAuthStateChanged's first emission
    // is that restored state, so waiting for it is what keeps the callable
    // from being called before an ID token exists. A `null` emission means
    // the session really is gone — __root.tsx's guard sends that visitor to
    // /login rather than this route guessing at a recovery.
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || hasStarted.current) return;
      hasStarted.current = true;

      void (async () => {
        try {
          const finishShopVibesConnect = httpsCallable<
            { code: string; state: string },
            { connected: boolean }
          >(functions, "finishShopVibesConnect");
          await finishShopVibesConnect({ code, state });
          navigate({
            to: "/settings/shopvibes",
            search: { shopvibes: "connected" },
            replace: true,
          });
        } catch (err: unknown) {
          const mapped = mapFirebaseError(err);
          const code =
            mapped instanceof AppError ? mapped.code : "shopvibes/unknown";
          // Only report what a human would actually want to look at: a uid
          // mismatch (an attack signal) or something unclassified.
          if (!EXPECTED_FAILURE_CODES.has(code)) {
            reportError(err, { context: "finishShopVibesConnect", code });
          }
          goToError(code);
        }
      })();
    });

    return () => unsubscribe();
  }, [code, state, error, navigate]);

  return (
    <div className="flex h-full w-full items-center justify-center px-4">
      <div
        className="flex flex-col items-center gap-3 text-center"
        role="status"
      >
        <svg
          aria-hidden="true"
          className="h-6 w-6 animate-spin text-gray-400"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <p className="text-sm text-gray-600">
          {t("shopvibes.settings.completing")}
        </p>
      </div>
    </div>
  );
};

export const Route = createFileRoute("/shopvibes/callback")({
  component: ShopVibesCallbackPage,
  validateSearch: (
    search: Record<string, unknown>
  ): ShopVibesCallbackSearch => ({
    code: typeof search.code === "string" ? search.code : undefined,
    state: typeof search.state === "string" ? search.state : undefined,
    error: typeof search.error === "string" ? search.error : undefined,
  }),
});
